// dependencies
import { Filler, LineElement, PointElement, Chart as PolygonChart, RadialLinearScale, Tooltip } from "chart.js";
import React from "react";
// charting components
import { Radar } from "react-chartjs-2";

import EmptyChart from "../../../../components/EmptyChart";
// components
import Spinner from "../../../../components/Spinner/QuarterCircle";
import Text from "../../../../components/Text";

// utils
import { getColor } from "../../../../utils/getColor";
import toFixed from "../../../../utils/toFixed";

// config
import Config from "../../Config";

// register charting components
PolygonChart.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip);

interface BaseProps {
	orderedDataSets: any[];
	isLoadingData: boolean;
	hasError: boolean;
	selectedCohort?: string;
}

const FactorsPolygonChart = (props: BaseProps) => {
	const { orderedDataSets, isLoadingData, hasError, selectedCohort = null } = props;
	/**
	 * If the sum of all averages is 0, we do not have enough data to display
	 */
	const valuesTotal = orderedDataSets.reduce((acc, dataset) => {
		const value = dataset.data.reduce((value, acc) => {
			return acc + value;
		}, 0);
		return acc + value;
	}, 0);

	if (hasError) {
		return (
			<Text size="paragraph-3" weight="regular">
				There was an error loading the factors summary.
			</Text>
		);
	}

	if (isLoadingData) {
		return <Spinner />;
	}

	if (valuesTotal === 0) {
		return <EmptyChart type="radar" />;
	} else {
		// sort labels alphabetically
		const labels = Config.FACTORS.FACTORS.sort();
		return (
			<Radar
				data={{
					labels,
					datasets: orderedDataSets,
				}}
				options={{
					responsive: true,
					maintainAspectRatio: false,
					spanGaps: true,
					animation: {
						duration: 0.2,
					},
					plugins: {
						legend: {
							display: false,
						},
						tooltip: {
							displayColors: false,
							backgroundColor: getColor("neutral", 7),
							bodyFont: {
								size: 12,
								family: "Source Sans Pro, sans-serif",
							},
							footerFont: {
								size: 12,
								family: "Source Sans Pro, sans-serif",
							},
							caretPadding: 8,
							cornerRadius: 4,
							// format non 0 numeric value to 2 decimal places, or default to formatted value
							callbacks: {
								title: () => {
									return "";
								},
								label: (context) => {
									if (context.raw === 0) {
										return "0";
									} else if (!context.dataset.label) {
										return [];
									} else if (typeof context.raw === "number") {
										return [context.dataset.label, `${context.label}: ${context.raw.toFixed(1)}`];
									} else {
										return [
											context.dataset.label,
											`${context.label}: ${toFixed(context.formattedValue, 1)}`,
										];
									}
								},
							},
							filter: (item: any) => {
								if (selectedCohort !== null) {
									return item.dataset.id === selectedCohort;
								}
								return true;
							},
						},
					},
					scales: {
						r: {
							min: 0,
							max: 5,
							angleLines: {
								display: false,
							},
							grid: {
								color: getColor("neutral", 2),
								lineWidth: 1,
							},
							pointLabels: {
								color: getColor("primary", 9),
								font: {
									size: 12,
									family: "Source Sans Pro",
									weight: 600,
									lineHeight: "16px",
								},
							},
							ticks: {
								stepSize: 1,
								display: false,
							},
						},
					},
				}}
			/>
		);
	}
};

export default FactorsPolygonChart;
